//------------------------------------------------
// Array Field formatter
// This handles the formatting of Array fields, specifically for CMS content arrays
// Pre-format: Converts expanded CMS content back to proper field values
// Post-format: Ensures array fields maintain correct structure for saving
//------------------------------------------------

import { isInternalType } from "../internal-type"

export default {
  type: "Array",
  preFormat: (fieldValues, Type) => {
    // Don't change fields inside internally used types
    if (isInternalType(Type)) return fieldValues
    // If Type is not provided or has no fields, return as-is
    if (!Type || !Type.fields) return fieldValues

    const fieldTypes = Type.fields

    for (const field of fieldTypes) {
      if (!field) continue

      // Handle both string type and object type {value: "Array", name: "Array"}
      const fieldType = typeof field.type === "object" && field.type?.value
        ? field.type.value
        : field.type

      if (fieldType !== "Array") continue

      const value = fieldValues[field.id]
      if (!Array.isArray(value)) continue

      // Determine if this is CMS content based on field definition only
      const itemType = field.item?.value || field.item

      // Only treat as CMS content if explicitly defined as such in field definition
      const isCMSContent = fieldType === "Content" ||
                          (fieldType === "Array" && field.itemType === "Reference")

      if (isCMSContent) {
        // Convert expanded CMS items back to simple structure for the field editor
        fieldValues[field.id] = value.map(item => {
          if (!item || typeof item !== 'object') return item
          
          if (item.content && typeof item.content === 'object') {
            // This is an expanded item from the database
            const label = item.content.name || item.content.title || item.content.label || ""
            return {
              _id: item._id,
              itemLabel: label,
              content: item.content,
              // Preserve metadata
              createdAt: item.createdAt,
              deleted: item.deleted,
              localization: item.localization
            }
          }
          
          // Simple structure - might be from field editor or database
          return {
            _id: item._id,
            itemLabel: item.label || item.itemLabel || "",
            ...item
          }
        })
      }
    }

    return fieldValues
  },
  
  postFormat: (fieldValues, Type) => {
    // Don't change fields inside internally used types
    if (isInternalType(Type)) return fieldValues
    // If Type is not provided or has no fields, return as-is
    if (!Type || !Type.fields) return fieldValues

    const fieldTypes = Type.fields

    for (const field of fieldTypes) {
      if (!field) continue

      const value = fieldValues[field.id]
      if (!Array.isArray(value)) continue

      // Determine if this is CMS content that needs to be simplified for database storage
      const fieldType = field.type?.value || field.type
      const itemType = field.item?.value || field.item

      // Handle both Array and Content types that reference CMS content
      // Only process arrays that explicitly have itemType "Reference" or field type "Content"
      // Don't process regular arrays that have custom field editors
      const isCMSContent = fieldType === "Content" ||
                          (fieldType === "Array" && field.itemType === "Reference")

      if (isCMSContent) {
        // Convert back to database format: ONLY _id and label (strip all other fields)
        fieldValues[field.id] = value.map(item => {
          if (item && typeof item === 'object' && item._id !== undefined) {
            const label = item.content?.name || item.content?.title ||
                         item.content?.label || item.itemLabel || item.label || ""
            // Return ONLY _id and label, explicitly excluding everything else
            return {
              _id: item._id,
              label: label
            }
          }
          return item
        }).filter(item => item && (item._id !== null && item._id !== undefined))
      }
    }

    return fieldValues
  }
}